/**
 * @fileoverview Front-end app router for Supabase Auth operations
 * @active Used for all authentication operations
 *
 * This service provides a centralized interface for authentication operations
 * including user signup, login, logout, and password management.
 *
 * @see {@link /src/types/userAuth.ts} - Type definitions
 * @see {@link /src/utils/authHelpers.ts} - Auth utilities
 * @see {@link /src/store/auth/auth.slice.ts} - Redux state
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  SignInParams,
  SignUpParams,
  OAuthParams,
  VerifyEmailParams,
  AuthSuccessResponse,
  CUSTOM_ERRORObject, // Corrected import name
  AuthProvider,
  PasswordParams,
} from "@/types/userAuth";
import {
  handleRTKError,
  createErrorObject,
  authProviders,
} from "@/types/userAuth";
import { createClientComponent } from "@/app/utils/supabase/client";

/**
 * Enhanced logging for auth operations
 * @param operation - Name of the operation
 * @param data - Data to log
 * @param isError - Whether this is an error log
 */
const logAuthOperation = (
  operation: string,
  data: any,
  isError = false
): void => {
  const timestamp = new Date().toISOString();
  const logPrefix = isError
    ? `[Auth ERROR][${operation}][${timestamp}]`
    : `[Auth][${operation}][${timestamp}]`;

  // Only log sensitive info in development
  const isDev = process.env.NEXT_ENVIRONMENT !== "production";
  const sanitizedData = isDev
    ? data
    : {
        ...data,
        // Redact sensitive fields in production
        ...(data.email && {
          email: `${data.email.split("@")[0].substring(0, 3)}...@${data.email.split("@")[1]}`,
        }),
        ...(data.password && { password: "********" }),
        ...(data.access_token && { access_token: "********" }),
      };

  if (isError) {
    console.error(logPrefix, sanitizedData);
  } else {
    console.log(logPrefix, sanitizedData);
  }
};

/**
 * Auth API with mutation endpoints for user authentication
 */
export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api" }),
  endpoints: (builder) => ({
    userSignIn: builder.mutation<AuthSuccessResponse, SignInParams>({
      async queryFn(credentials) {
        try {
          logAuthOperation("signIn", {
            email: credentials.email,
            attemptTime: new Date(),
          });

          const supabase = createClientComponent();
          const { data, error } = await supabase.auth.signInWithPassword({
            email: credentials.email,
            password: credentials.password,
          });

          if (error) {
            logAuthOperation(
              "signIn",
              { error, email: credentials.email },
              true
            );
            throw createErrorObject(error.message, error.name, 400);
          }

          logAuthOperation("signIn", {
            success: true,
            userId: data.user?.id,
            email: credentials.email,
          });

          const response: AuthSuccessResponse = {
            success: true,
            message: "Successfully signed in",
            user: data.user,
            session: data.session,
          };

          if (credentials.onClose) {
            credentials.onClose();
          }

          return { data: response };
        } catch (error) {
          logAuthOperation("signIn", { error }, true);
          return { error: handleRTKError(error) };
        }
      },
    }),

    userSignUp: builder.mutation<AuthSuccessResponse, SignUpParams>({
      query: (credentials) => {
        logAuthOperation("signUp (API Call)", {
          email: credentials.email,
          firstName: credentials.firstName,
          lastName: credentials.lastName,
          metadata: credentials.metadata,
        });
        // Ensure password is included if available, API route handles logic
        const body = { ...credentials };
        return {
          url: "/auth/signup", // Target the internal API route
          method: "POST",
          body: body,
        };
      },
      // Handle potential errors from the API route
      transformErrorResponse: (response) => {
        logAuthOperation("signUp (API Error)", { response }, true);
        return handleRTKError(response.data || response);
      },
    }),

    userSignOut: builder.mutation<AuthSuccessResponse, void>({
      async queryFn() {
        try {
          logAuthOperation("signOut", { attemptTime: new Date() });

          const supabase = createClientComponent();
          const { error } = await supabase.auth.signOut();

          if (error) {
            logAuthOperation("signOut", { error }, true);
            throw createErrorObject(error.message, error.name, 400);
          }

          logAuthOperation("signOut", { success: true });

          return {
            data: {
              success: true,
              message: "Successfully signed out",
            },
          };
        } catch (error) {
          logAuthOperation("signOut", { error }, true);
          return { error: handleRTKError(error) };
        }
      },
    }),

    oauthSignIn: builder.mutation<
      AuthSuccessResponse,
      { provider: AuthProvider } & OAuthParams
    >({
      async queryFn({ provider, ...params }) {
        try {
          const supabase = createClientComponent();
          const { data, error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
              skipBrowserRedirect: params.is_connect,
              ...(params.access_token
                ? { access_token: params.access_token }
                : {}),
              ...(params.authCode
                ? { queryParams: { code: params.authCode } }
                : {}),
            },
          });

          if (error) {
            throw createErrorObject(error.message, error.name, 400);
          }

          const response: AuthSuccessResponse = {
            success: true,
            message: "OAuth sign-in initiated",
            provider,
            redirected: true,
            url: data.url,
          };

          if (params.onClose) {
            params.onClose();
          }

          return { data: response };
        } catch (error) {
          return { error: handleRTKError(error) };
        }
      },
    }),

    verifyEmail: builder.mutation<AuthSuccessResponse, VerifyEmailParams>({
      async queryFn({ email, token, type }) {
        try {
          const supabase = createClientComponent();
          const { data, error } = await supabase.auth.verifyOtp({
            email,
            token,
            type,
          });

          if (error) {
            throw createErrorObject(error.message, error.name, 400);
          }

          return {
            data: {
              success: true,
              message: "Email verified successfully",
              user: data.user,
              session: data.session,
            },
          };
        } catch (error) {
          return { error: handleRTKError(error) };
        }
      },
    }),

    forgotPassword: builder.mutation<AuthSuccessResponse, string>({
      async queryFn(email) {
        try {
          const supabase = createClientComponent();
          const { data, error } = await supabase.auth.resetPasswordForEmail(
            email,
            {
              redirectTo: `${window.location.origin}/reset-password`,
            }
          );

          if (error) {
            throw createErrorObject(error.message, error.name, 400);
          }

          return {
            data: {
              success: true,
              message: "Password reset email sent successfully",
            },
          };
        } catch (error) {
          return { error: handleRTKError(error) };
        }
      },
    }),

    resendEmail: builder.mutation<AuthSuccessResponse, string>({
      async queryFn(email) {
        try {
          const supabase = createClientComponent();
          const { data, error } = await supabase.auth.resend({
            type: "signup",
            email,
          });

          if (error) {
            throw createErrorObject(error.message, error.name, 400);
          }

          return {
            data: {
              success: true,
              message: "Verification email resent successfully",
            },
          };
        } catch (error) {
          return { error: handleRTKError(error) };
        }
      },
    }),

    /**
     * Generalized provider auth mutation that works with any supported provider
     * Handles authentication with a specific provider using access_token and authCode
     *
     * @param provider - The provider to authenticate with (e.g., "google", "linkedin_oidc")
     * @param access_token - The access token from the provider
     * @param authCode - The authorization code from the provider
     * @param onClose - Optional callback when authentication is complete
     * @returns Authentication response with provider information
     */
    providerAuth: builder.mutation<
      AuthSuccessResponse,
      {
        provider: AuthProvider;
        access_token: string;
        authCode: string;
        onClose?: () => void;
      }
    >({
      async queryFn({ provider, access_token, authCode, onClose }) {
        try {
          const supabase = createClientComponent();
          const { data, error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
              queryParams: {
                code: authCode,
                access_token,
              },
              skipBrowserRedirect: true,
            },
          });

          if (error) {
            throw createErrorObject(error.message, error.name, 400);
          }

          const response: AuthSuccessResponse = {
            success: true,
            message: `Successfully authenticated with ${provider}`,
            provider,
            redirected: false,
          };

          if (onClose) {
            onClose();
          }

          return { data: response };
        } catch (error) {
          return { error: handleRTKError(error) };
        }
      },
    }),

    /**
     * REMOVED: Deprecated provider-specific auth methods
     * Use providerAuth instead with appropriate provider parameter
     * Example:
     * ```
     * providerAuth({ provider: authProviders.google, ... })
     * providerAuth({ provider: authProviders.linkedin, ... })
     * ```
     */

    addPassword: builder.mutation<AuthSuccessResponse, PasswordParams>({
      async queryFn(params) {
        try {
          const { password, accessToken, email } = params;

          if (!email || !accessToken) {
            throw createErrorObject(
              "Missing required fields",
              "VALIDATION_ERROR",
              400
            );
          }

          // Create a client instance with admin API key for token operations
          const supabase = createClientComponent();

          // Update user password directly
          const { data, error } = await supabase.auth.updateUser({
            password,
          });

          if (error) {
            throw createErrorObject(error.message, error.name, 400);
          }

          return {
            data: {
              success: true,
              message: "Password updated successfully",
              user: data.user,
            },
          };
        } catch (error) {
          console.error("Password update failed:", error);
          return { error: handleRTKError(error) };
        }
      },
    }),

    signInWithMagicLink: builder.mutation<
      AuthSuccessResponse,
      { email: string }
    >({
      async queryFn({ email }) {
        try {
          const supabase = createClientComponent();
          const { error } = await supabase.auth.signInWithOtp({
            email: email,
            options: {
              // emailRedirectTo is optional, Supabase defaults to SITE_URL
              // shouldCreateUser: false, // Don't create user if they don't exist
            },
          });

          if (error) {
            throw createErrorObject(error.message, error.name, 400);
          }

          return {
            data: {
              success: true,
              message: "Magic link sent successfully.",
            },
          };
        } catch (error) {
          return { error: handleRTKError(error) };
        }
      },
    }),

    /**
     * Adds an email to the waitlist via the signup API route.
     */
    addToWaitlist: builder.mutation<AuthSuccessResponse, { email: string }>({
      query: ({ email }) => {
        logAuthOperation("addToWaitlist (API Call)", { email });
        return {
          url: "/auth/signup", // Use the same signup route
          method: "POST",
          body: { email, isWaitlist: true }, // Add the flag
        };
      },
      transformErrorResponse: (response) => {
        logAuthOperation("addToWaitlist (API Error)", { response }, true);
        return handleRTKError(response.data || response);
      },
    }),

    /**
     * Free account creation by email (sends magic link)
     * Similar to signInWithMagicLink but allows user creation
     */
    freeAccountByEmail: builder.mutation<
      AuthSuccessResponse,
      { email: string }
    >({
      async queryFn({ email }) {
        try {
          const supabase = createClientComponent();
          const { error } = await supabase.auth.signInWithOtp({
            email: email,
            options: {
              // Allow user creation if they don't exist
              shouldCreateUser: true,
              emailRedirectTo: `${window.location.origin}/auth/callback`,
            },
          });

          if (error) {
            throw createErrorObject(error.message, error.name, 400);
          }

          return {
            data: {
              success: true,
              message: "Magic link sent successfully. Please check your email.",
            },
          };
        } catch (error) {
          return { error: handleRTKError(error) };
        }
      },
    }),
  }),
});

// Extract the hooks from the API
const extractedHooks = authApi.endpoints;

// Export standard hooks
export const {
  userSignIn: { useMutation: useUserSignInMutation },
  userSignUp: { useMutation: useUserSignUpMutation },
  userSignOut: { useMutation: useUserSignOutMutation },
  oauthSignIn: { useMutation: useOauthSignInMutation },
  verifyEmail: { useMutation: useVerifyEmailMutation },
  forgotPassword: { useMutation: useForgotPasswordMutation },
  resendEmail: { useMutation: useResendEmailMutation },
  providerAuth: { useMutation: useProviderAuthMutation },
  addPassword: { useMutation: useAddPasswordMutation },
  signInWithMagicLink: { useMutation: useSignInWithMagicLinkMutation },
  freeAccountByEmail: { useMutation: useFreeAccountByEmailMutation },
  addToWaitlist: { useMutation: useAddToWaitlistMutation }, // Export the new hook
} = extractedHooks;

// Re-export provider-specific hooks for backward compatibility
export const useGoogleAuthMutation = (options?: any) => {
  const [providerAuth, results] = useProviderAuthMutation(options);

  // Return a wrapped function that provides the Google provider by default
  return [
    (params: Omit<Parameters<typeof providerAuth>[0], "provider">) =>
      providerAuth({ ...params, provider: authProviders.google }),
    results,
  ] as const;
};

export const useLinkedinAuthMutation = (options?: any) => {
  const [providerAuth, results] = useProviderAuthMutation(options);

  // Return a wrapped function that provides the LinkedIn provider by default
  return [
    (params: Omit<Parameters<typeof providerAuth>[0], "provider">) =>
      providerAuth({ ...params, provider: authProviders.linkedin }),
    results,
  ] as const;
};
